import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { McpOAuthGrant } from '@/api/v4/mcp-oauth.api';
import { getMessageFromErrorResponse } from '@/api/v4/base-fetch';
import { Loader } from '@/components/loader/loader';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { notify } from '@/ui/snackbar/notify';
import { useUserAccount } from '@/user/use-user-account';

import { ClaudeMcpConnectorConnectedView } from './claude-mcp-connector-connected-view';
import { ClaudeMcpConnectorErrorView } from './claude-mcp-connector-error-view';
import { ClaudeMcpConnectorNotConnectedView } from './claude-mcp-connector-not-connected-view';
import { ClaudeMcpConnectorWaitingView } from './claude-mcp-connector-waiting-view';
import { Card } from './claude-mcp-connector.styles';
import {
  getConnectionStatus,
  type ConnectionStatus,
} from '../mcp-oauth/mcp-oauth-connection-status';
import {
  buildAccountScopedMcpConnectorConfig,
  buildFallbackMcpConnectorConfig,
} from '../mcp-oauth/mcp-oauth-connector-config';
import {
  isSupportedMcpOAuthEnvironment,
  resolveMcpOAuthDisplayEnvironment,
  resolveMcpOAuthEnvironment,
} from '../mcp-oauth/mcp-oauth-environment';
import { useMcpOAuthConnect } from '../mcp-oauth/use-mcp-oauth-connect.mutation';
import { useMcpOAuthFieldCopy } from '../mcp-oauth/use-mcp-oauth-field-copy';

export type { ConnectionStatus };
export { getConnectionStatus };

interface ClaudeMcpConnectorCardProps {
  grant: McpOAuthGrant | null;
  isLoading: boolean;
  onDisconnect: (grant: McpOAuthGrant) => void;
}

export const ClaudeMcpConnectorCard = ({
  grant,
  isLoading,
  onDisconnect,
}: ClaudeMcpConnectorCardProps) => {
  const { t } = useTranslation('settings', { keyPrefix: 'accessTokensTab' });
  const account = useUserAccount();
  const [showSetupSteps, setShowSetupSteps] = useState(false);
  const setupStepsRef = useRef<HTMLDivElement | null>(null);
  const { copiedFieldId, copyValue } = useMcpOAuthFieldCopy();
  const { connect, isConnecting } = useMcpOAuthConnect();

  const resolvedEnvironment = useMemo(() => resolveMcpOAuthEnvironment(), []);
  const displayEnvironment = useMemo(
    () => resolveMcpOAuthDisplayEnvironment(),
    [],
  );
  const connectorConfig = useMemo(() => {
    if (!account) {
      return buildFallbackMcpConnectorConfig(displayEnvironment);
    }

    return buildAccountScopedMcpConnectorConfig({
      account,
      connectorName: t('mcpOAuth.connectorName', { accountName: account.name }),
      environment: displayEnvironment,
    });
  }, [account, displayEnvironment, t]);
  const isEnvironmentSupported =
    isSupportedMcpOAuthEnvironment(resolvedEnvironment);
  const connectionStatus = getConnectionStatus(grant);
  const showAdvancedSettingsHint = displayEnvironment.kind === 'staging';

  const handleConnect = (): void => {
    void connect().catch((error: unknown) => {
      notify({
        message:
          getMessageFromErrorResponse(error) ?? t('mcpOAuth.connectError'),
      });
    });
  };

  if (isLoading) {
    return (
      <Card>
        <Flexbox fullWidth justify="center" padding="24px 0">
          <Loader />
        </Flexbox>
      </Card>
    );
  }

  if (connectionStatus === 'error' && grant) {
    return (
      <ClaudeMcpConnectorErrorView
        connectorConfig={connectorConfig}
        copiedFieldId={copiedFieldId}
        grant={grant}
        isConnecting={isConnecting}
        onCopy={copyValue}
        onDisconnect={onDisconnect}
        onReconnect={handleConnect}
        setupStepsRef={setupStepsRef}
        showAdvancedSettingsHint={showAdvancedSettingsHint}
      />
    );
  }

  if (connectionStatus === 'connected' && grant) {
    return (
      <ClaudeMcpConnectorConnectedView
        connectorConfig={connectorConfig}
        copiedFieldId={copiedFieldId}
        grant={grant}
        onCopy={copyValue}
        onDisconnect={onDisconnect}
        onToggleSetupSteps={() => {
          setShowSetupSteps(current => !current);
        }}
        setupStepsRef={setupStepsRef}
        showAdvancedSettingsHint={showAdvancedSettingsHint}
        showSetupSteps={showSetupSteps}
      />
    );
  }

  if (connectionStatus === 'waiting') {
    return (
      <ClaudeMcpConnectorWaitingView
        connectorConfig={connectorConfig}
        copiedFieldId={copiedFieldId}
        onCopy={copyValue}
        setupStepsRef={setupStepsRef}
        showAdvancedSettingsHint={showAdvancedSettingsHint}
      />
    );
  }

  return (
    <ClaudeMcpConnectorNotConnectedView
      connectorConfig={connectorConfig}
      copiedFieldId={copiedFieldId}
      displayEnvironment={displayEnvironment}
      isConnecting={isConnecting}
      isEnvironmentSupported={isEnvironmentSupported}
      onConnect={handleConnect}
      onCopy={copyValue}
      showAdvancedSettingsHint={showAdvancedSettingsHint}
    />
  );
};
